/**
 * Data Region definitions (feature 021-china-account-access).
 *
 * A "data region" determines which regional backend/account the portal talks
 * to. The API contract is identical across regions; only the destination
 * (endpoints/account) changes. See spec:
 * specs/021-china-account-access/spec.md
 */

export const DATA_REGIONS = {
  GLOBAL: 'global',
  CHINA: 'china',
};

export const DEFAULT_DATA_REGION = DATA_REGIONS.GLOBAL;

/**
 * Cognito group that grants access to the China data region (Phase 1 source of
 * truth). Membership in this group ⇒ canAccessChina = true.
 */
export const CHINA_ACCESS_GROUP = 'china-access';

/**
 * localStorage key used to persist the selected data region across reloads.
 */
export const DATA_REGION_STORAGE_KEY = 'dataRegion';

export const DATA_REGION_OPTIONS = [
  { value: DATA_REGIONS.GLOBAL, label: 'Global' },
  { value: DATA_REGIONS.CHINA, label: 'China' },
];

/**
 * Returns true if the given region value is a known, valid data region.
 * @param {string} region
 * @returns {boolean}
 */
export const isValidDataRegion = (region) => Object.values(DATA_REGIONS).includes(region);

/**
 * "Category A" feature keys that are NOT available in the China data region by
 * design (feature 021-china-account-access). The backend already returns
 * `CHINA_NOT_CONFIGURED:<name>` for these; the UI hides their entry points so
 * users are never offered actions that cannot succeed in China.
 */
export const CHINA_UNAVAILABLE_FEATURES = [
  'admin',
  'platformHealth',
  'dataDictionary',
  'deviceDefender',
  'rubiqAgent',
  'deviceOnboarding',
  'dataReading',
  'messageSimulator',
  'certificateManagement',
  'mqttDashboard',
  'deviceAnalytics',
  'favouriteCredentials',
  'assetTypes',
  'deploymentFileTypes',
  'machineKpis',
];

/**
 * Returns true if the given feature key is available in the given data region.
 * Only the China region restricts features; every feature is available in
 * Global (and for any unknown/undefined region we default to available).
 * @param {string} featureKey - One of the CHINA_UNAVAILABLE_FEATURES keys.
 * @param {string} region - A DATA_REGIONS value.
 * @returns {boolean}
 */
export const isFeatureAvailableInRegion = (featureKey, region) =>
  region === DATA_REGIONS.CHINA ? !CHINA_UNAVAILABLE_FEATURES.includes(featureKey) : true;
